/**
 * useXlsxAttachment
 *
 * Builds the XLSX workbook that `FormEmailModal` and the workflow email
 * parameter attach to a message — the only XLSX producer whose output leaves the
 * app, so it is the one that must never emit a row the recipient's view
 * withholds (SYD-2279).
 *
 * `visibilityGate` is the shared `resolveExportVisibilityGate` predicate over the
 * *effective* view (the override beats the on-screen one), exposed so callers can
 * refuse before assembling an email; `generateXlsxAttachment` throws on the same
 * gate. It replaces a `connect({ onError })` channel that the shared
 * `parameters/organization` singleton never routed here.
 *
 * The tables the workbook carries come from `useVisibleTableIds` over the single
 * effective view, the same hook `WorkflowParameterContent` reads for its
 * split-column pre-flight (SYD-2412), so the check and the export cannot drift
 * apart on which tables a view renders.
 *
 * The split is read twice, deliberately: the reactive `splitQuery` from
 * `useFormStore()` drives the live `splitRowCounts` / `tableOptions` preview,
 * while `generateXlsxAttachment` takes `params.splitQuery` — a per-target scope
 * (SYD-2302) — and otherwise reads the store once at click time, never through
 * a render closure (SYD-2252/2264). Do not collapse the two.
 */

import { useContext, useMemo, useState, useEffect, useRef } from 'react';
import { snapshot, useSnapshot } from 'valtio';
import { collection, doc, getCountFromServer, getDoc, getDocs, orderBy, query } from 'firebase/firestore';
import { firestore } from '@/services/startFirestore';
import { useOrganizationParameters } from '@/use/useOrganizationParameters';
import { resolveExportVisibilityGate } from '@/use/resolveExportVisibilityGate';
import { useVisibilityMode } from '@/use/useVisibilityMode';
import { formStore, useFormStore } from '@/store';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import { useVisibleTableIds } from '@/use/form/useVisibleTableIds';
import { elementVisibility } from '@/components/rendering/elementVisibility';
import { resolveSplitForTable } from '@/components/ArTable/split/resolveSplitForTable';
import { filterArRowIdsBySplit } from '@/components/ArTable/split/filterArRowIdsBySplit';
import createFormEmailHeaderRows from '../create/createFormEmailHeaderRows';
import { createXlsxAttachment } from '../create/createXlsxAttachment';
import createXlsxWorkbookBlob from '../create/createXlsxWorkbookBlob';
import fetchXlsxLogoImage from '../create/fetchXlsxLogoImage';
import discoverArTablesInLayoutStructure from '../create/discoverArTablesInLayoutStructure';
import createArTableExportSnapshot from '../create/createArTableExportSnapshot';
import type { SplitQuery, UIElement } from '#types/System.types';
import type { VisibilityTypeId } from '#types/System.types';
import type { ArColumnDefinition, ArRowData, ArRowId, ArTableType } from '#types/System.Artifacts.types';
import type { DocEntry } from '@/store/Store.types';
import { arTableRowsPath } from '#types/System.Artifacts.types';

type XlsxTableOption = {
  key: string;
  label: string;
  rowCount: number;
};

type GenerateParams = {
  selectedKeys: string[] | undefined;
  formNumber?: string;
  filenameBase: string;
  splitQuery?: SplitQuery;
};

type UseXlsxAttachmentOptions = {
  visibilityModeOverride?: VisibilityTypeId;
};

export const useXlsxAttachment = (options: UseXlsxAttachmentOptions = {}) => {
  const { visibilityModeOverride } = options;
  const {
    data: orgParams,
    loading: orgParamsLoading,
    error: orgParamsError,
    getVisibilityTypesForFormType,
  } = useOrganizationParameters();

  const { form, formType, formVersion, splitQuery } = useFormStore();
  const visibilityMode = useVisibilityMode();
  const { fieldTypes } = useContext(FieldTypeContext);

  const formId = form?.data?.docId;
  const versionId = formVersion?.data?.docId;
  const tablesSnap = useSnapshot(formStore.state.tables);

  const layoutStructure = formType.data?.defaultUILayout?.structure as UIElement | undefined;
  const effectiveVisibilityMode: VisibilityTypeId | undefined = visibilityModeOverride ?? visibilityMode;
  const effectiveVisibilityType = useMemo(() => {
    if (!effectiveVisibilityMode || effectiveVisibilityMode === 'internal') return undefined;
    return getVisibilityTypesForFormType(formType.data?.docId).find((v) => v.id === effectiveVisibilityMode);
  }, [getVisibilityTypesForFormType, formType.data?.docId, effectiveVisibilityMode]);

  const visibilityGate = resolveExportVisibilityGate({
    visibilityMode: effectiveVisibilityMode,
    orgParams,
    orgParamsLoading,
    orgParamsError,
  });

  const exportVisibilityModes = useMemo(() => [effectiveVisibilityMode], [effectiveVisibilityMode]);
  const visibleTableIds = useVisibleTableIds(exportVisibilityModes);

  const [rowCounts, setRowCounts] = useState<Record<string, number>>({});
  const [tableLabels, setTableLabels] = useState<Record<string, string>>({});
  const [splitRowCounts, setSplitRowCounts] = useState<Record<string, number>>({});

  const splitKey = splitQuery
    ? `${splitQuery.mode ?? ''}|${splitQuery.splitRef?.valuePath ?? ''}|${String(splitQuery.queryValue ?? '')}`
    : '';
  const splitFilteredCountCache = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    if (!formId || !versionId || visibleTableIds.length === 0) return;

    const cancelled = { current: false };
    const load = async () => {
      const results = await Promise.all(
        visibleTableIds.map(async (tableId) => {
          try {
            const rowsRef = collection(firestore, arTableRowsPath(`forms/${formId}/formVersions/${versionId}`, tableId));
            const snap = await getCountFromServer(query(rowsRef));
            return [tableId, snap.data().count] as const;
          } catch {
            return [tableId, 0] as const;
          }
        })
      );
      if (!cancelled.current) setRowCounts(Object.fromEntries(results));
    };
    void load();
    return () => { cancelled.current = true; };
  }, [formId, versionId, visibleTableIds]);

  useEffect(() => {
    if (visibleTableIds.length === 0) return;

    const cancelled = { current: false };
    const load = async () => {
      const results = await Promise.all(
        visibleTableIds.map(async (tableId) => {
          try {
            const schemaRef = doc(firestore, 'tableTypes', tableId);
            const schemaSnap = await getDoc(schemaRef);
            const data = schemaSnap.data() as { description?: { shortLabel?: string } } | undefined;
            return [tableId, data?.description?.shortLabel ?? tableId] as const;
          } catch {
            return [tableId, tableId] as const;
          }
        })
      );
      if (!cancelled.current) setTableLabels(Object.fromEntries(results));
    };
    void load();
    return () => { cancelled.current = true; };
  }, [visibleTableIds]);

  useEffect(() => {
    if (!formId || !versionId || !splitQuery || visibleTableIds.length === 0) {
      setSplitRowCounts({});
      return;
    }

    const cancelled = { current: false };
    const load = async () => {
      const parentPath = `forms/${formId}/formVersions/${versionId}`;
      const results = await Promise.all(
        visibleTableIds.map(async (tableId) => {
          const cacheKey = `${parentPath}:${tableId}|${splitKey}`;
          const cached = splitFilteredCountCache.current.get(cacheKey);
          if (cached !== undefined) return [tableId, cached] as const;

          try {
            const schemaRef = doc(firestore, 'tableTypes', tableId);
            const schemaSnap = await getDoc(schemaRef);
            const schema = schemaSnap.data() as ArTableType | undefined;
            const columns = (schema?.columns ?? []) as ArColumnDefinition[];
            const split = resolveSplitForTable({ columns, splitQuery });
            if (!split) return [tableId, rowCounts[tableId] ?? 0] as const;

            const rowsRef = collection(firestore, arTableRowsPath(parentPath, tableId));
            const rowsSnap = await getDocs(query(rowsRef, orderBy('order', 'asc')));
            const rowsMap = new Map<ArRowId, DocEntry<ArRowData>>();
            const rowIds: ArRowId[] = [];
            rowsSnap.forEach((d) => {
              const id = d.id as ArRowId;
              rowIds.push(id);
              rowsMap.set(id, { data: d.data() as ArRowData });
            });
            const filtered = filterArRowIdsBySplit({ rowIds, rowsDocs: rowsMap, split });
            splitFilteredCountCache.current.set(cacheKey, filtered.length);
            return [tableId, filtered.length] as const;
          } catch {
            return [tableId, 0] as const;
          }
        }),
      );
      if (!cancelled.current) setSplitRowCounts(Object.fromEntries(results));
    };
    void load();
    return () => { cancelled.current = true; };
  }, [formId, versionId, visibleTableIds, splitQuery, splitKey, rowCounts]);

  const tableOptions = useMemo<XlsxTableOption[]>(() => {
    if (!formId || !versionId) {
      return visibleTableIds.map((tableId) => ({
        key: tableId,
        label: tableLabels[tableId] ?? tableId,
        rowCount: rowCounts[tableId] ?? 0,
      }));
    }
    const parentPath = `forms/${formId}/formVersions/${versionId}`;
    return visibleTableIds.map((tableId) => {
      const slot = tablesSnap.get(formStore.tableKey(parentPath, tableId));
      const selectedCount = slot?.ui.selection.selectedCount ?? 0;
      const splitCount = splitQuery ? splitRowCounts[tableId] : undefined;
      const fallbackCount = splitCount ?? rowCounts[tableId] ?? 0;
      return {
        key: tableId,
        label: tableLabels[tableId] ?? tableId,
        rowCount: selectedCount > 0 ? selectedCount : fallbackCount,
      };
    });
  }, [visibleTableIds, rowCounts, tableLabels, formId, versionId, tablesSnap, splitQuery, splitRowCounts]);

  const generateXlsxAttachment = async (params: GenerateParams) => {
    if (visibilityGate) {
      throw new Error(visibilityGate.message);
    }

    const fields = formVersion?.data?.fields;

    if (!layoutStructure) {
      throw new Error('No form layout found for XLSX export.');
    }
    if (!fields) {
      throw new Error('No form fields found for XLSX export.');
    }

    const formId = form.data?.docId;
    const versionId = formVersion.data?.docId;
    if (!formId) {
      throw new Error('No formId available for XLSX export.');
    }
    if (!versionId) {
      throw new Error('No formVersionId available for XLSX export.');
    }

    const headerRows = createFormEmailHeaderRows({
      formNumber: params.formNumber,
      orgName: orgParams?.name,
      orgEmail: orgParams?.email,
      orgPhone: orgParams?.phone,
      layoutStructure,
      fields,
      fieldTypes,
      visibilityMode: effectiveVisibilityMode,
    });

    const allTables = discoverArTablesInLayoutStructure(layoutStructure);
    const visibleTables = effectiveVisibilityMode
      ? allTables.filter((t) => elementVisibility(t, effectiveVisibilityMode))
      : allTables;
    const selectedSet = new Set(params.selectedKeys ?? []);
    const chosenTables =
      selectedSet.size > 0 ? visibleTables.filter((t) => selectedSet.has(t.tableId)) : visibleTables;

    if (chosenTables.length === 0) {
      throw new Error('No tables available for XLSX export.');
    }

    const parentPath = `forms/${formId}/formVersions/${versionId}`;
    const exportSplitQuery =
      params.splitQuery ?? (snapshot(formStore.state).splitQuery as SplitQuery | undefined);
    const tableSnapshots = await Promise.all(chosenTables.map((table) => {
      const slot = formStore.getTableSlot(parentPath, table.tableId);
      const selectedRowIds = slot
        ? (snapshot(slot.ui.selection).selectedRowIds as ArRowId[])
        : null;
      return createArTableExportSnapshot({
        formId,
        versionId,
        table,
        visibilityMode: effectiveVisibilityMode,
        visibilityType: effectiveVisibilityType,
        fieldTypes,
        splitQuery: exportSplitQuery,
        selectedRowIds,
        formKitRowColumnCalc: formType.data?.kitRowColumnCalc,
      });
    }));

    const today = new Date().toISOString().split('T')[0];
    const filename = `${params.filenameBase}-${today}.xlsx`;

    const logoImage = await fetchXlsxLogoImage(orgParams?.logoUrl);

    const { blob, filename: xlsFilename } = await createXlsxWorkbookBlob({
      headerRows,
      tableSnapshots,
      filename,
      logoImage,
      logoExportSizeMm: orgParams?.logoExportSize,
    });

    return createXlsxAttachment({ xlsBlob: blob, filename: xlsFilename });
  };

  return {
    generateXlsxAttachment,
    tableOptions,
    visibilityGate,
  };
};

